import React, { Component, PropTypes } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Link } from 'react-router';

import uploadFile from '../utils/upload-file.js';

import AvatarForm from '../components/AvatarForm.js';
import PasswordInput from '../components/PasswordInput.js';

import './AccountPage.css';

// Import actions
import * as sessionActions from '../actions/session-actions';

class AccountPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      name: this.props.session.user.name || '',
      displayName: this.props.session.user.displayName || '',
      email: this.props.session.user.email || '',

      changedAvatar: false,
      avatar: this.props.session.user.avatar,

      password: '',
      newPassword: '',
      newPasswordConfirm: ''
    }

    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.formValid = this.formValid.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    const { session } = nextProps;
    // User was successfully updated, clear out the password fields.
    if (session.updatedUser) {
      this.setState({
        password: '',
        newPassword: '',
        newPasswordConfirm: ''
      });
    }
  }

  handleInputChange(event) {
    const { target } = event;
    const { value, name } = target;

    // Update the state key of the input's `name` with the new value.
    this.setState({
      [name]: value
    });
  }

  /**
   * Called when a new avatar is selected by the user.
   * Is passed a Blob object for the chosen file, or null when the user deletes their avatar.

   * @param {Blob} blob The file blob of the new avatar.
   * @memberof AccountPage
   */
  handleAvatarChange = (blob) => {
    this.setState({
      changedAvatar: true,
      avatar: blob
    });
  }

  handleSubmit(ev) {
    ev.preventDefault();
    const { name, displayName, email, password, newPassword, newPasswordConfirm } = this.state;
    let payload = { name, displayName, email, password, newPassword, newPasswordConfirm };

    // Delete any null keys from the payload.
    Object.keys(payload).forEach((key) => (payload[key] == null) && delete payload[key]);

    let avatarUpload = Promise.resolve();

    // If the avatar is being changed, change the avatarUpload promise to the actual
    // upload process.
    const { changedAvatar, avatar } = this.state;
    if (changedAvatar && avatar) {
      avatarUpload = uploadFile(avatar, this.props.session.token, { hideFromBackpack: true });
    }

    // If the user didn't supply their current password, don't send up any
    // password values.
    if (!password) {
      delete payload['password'];
      delete payload['newPassword'];
      delete payload['newPasswordConfirm'];
    }

    avatarUpload
      .then((file) => {
        if (changedAvatar) {
          // If the avatar has been changed, set the payload avatar to the file ID (if
          // the change is adding a new avatar), or null (if the change is deleting your
          // avatar).
          payload.avatar = file ? file.id : null;
        }

        return this.props.actions.requestUpdateUser(payload);
      });
  }

  /**
   * Helper for uploading a given avatar Blob. Returns a promise that either resolves
   * with the object for the newly created avatar file, or rejects with the error returned
   * from the service.
   *
   * @param {Blob} blob The blob for the file.
   * @memberof AccountPage
   */
  uploadAvatar(file) {
    if (!file || !(file instanceof Blob)) {
      throw new Error(`Improper file given to uploadAvatar() method, instead was given: ${typeof file} ${file}`);
    }

    uploadFile(file, this.props.session.token)
      .then((file) => {
        console.log('Upload finished!', file);
      }).catch((err) => {
        console.error('Upload failed.', err);
      });
  }

  formValid() {
    const { password, newPassword, newPasswordConfirm } = this.state;
    // If they've filled out any of the password fields, make sure they're all
    // filled out, and that the new password meets the requirements.
    if (password || newPassword || newPasswordConfirm) {
      let hasAllPasswordFields = password.length && newPassword.length && newPasswordConfirm.length;
      let passwordMeetsRequirements = newPassword.length >= 7;
      let passwordsMatch = newPassword === newPasswordConfirm;

      return hasAllPasswordFields && passwordMeetsRequirements && passwordsMatch;
    }

    return true;
  }

  render() {
    let updatingUserError;
    if (this.props.session.updatingUserError) {
      updatingUserError = <p>We couldn't update your profile at this time. Reason: {this.props.session.updatingUserError.description}</p>
    }

    let submitLabel = 'Save changes';
    if (this.props.session.updatingUser) {
      submitLabel = 'Saving changes...';
    }

    return (
      <div className="AccountPage container">
        <div className="row">
          <div className="col-md-10 col-md-push-1">
            <h1>Your Account</h1>

            <form className="AccountForm row" onSubmit={this.handleSubmit} action="#">
              <div className="col-sm-5">
                <label>Avatar</label>
                <AvatarForm avatar={this.state.avatar} onAvatarSelect={this.handleAvatarChange} />

                <label htmlFor="name">Name</label>
                <input type="text" name="name" className="form-input form-input--fullwidth" placeholder="name" onChange={this.handleInputChange} value={this.state.name} />

                <label htmlFor="displayName">Display name</label>
                <input type="text" name="displayName" className="form-input form-input--fullwidth" placeholder="display name" onChange={this.handleInputChange} value={this.state.displayName} />

                <label htmlFor="email">Email</label>
                <input type="text" name="email" className="form-input form-input--fullwidth" placeholder="email" onChange={this.handleInputChange} value={this.state.email} />
              </div>

              <div className="col-sm-6 col-sm-push-1">
                <p className="AccountForm__password-help"><small>Optional unless you're changing your password.</small></p>
                <div className="AccountForm__password-section">
                  <label htmlFor="name">Current password</label>
                  <input className="form-input form-input--fullwidth"
                    type="password"
                    name="password"
                    placeholder="password"
                    onChange={this.handleInputChange}
                    value={this.state.password} />

                  <label htmlFor="name">New password</label>
                  <PasswordInput inputClassName="form-input form-input--fullwidth"
                    name="newPassword"
                    lightVariant={true}
                    value={this.state.newPassword}
                    onChange={this.handleInputChange}
                    placeholder="new password" />

                  <label htmlFor="name">Confirm password</label>
                  <input type="password"
                    className="form-input form-input--fullwidth"
                    name="newPasswordConfirm"
                    placeholder="confirm password"
                    value={this.state.newPasswordConfirm}
                    onChange={this.handleInputChange} />
                </div>

                <div>
                  {updatingUserError}
                </div>

                <input type="submit" className="Button Button--primary" value={submitLabel} disabled={this.props.session.updatingUser || !this.formValid()} />
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }
};

AccountPage.propTypes = {
  actions: PropTypes.object.isRequired,
  session: PropTypes.object.isRequired,
};

function mapStateToProps(state, props) {
  return {
    session: state.session
  };
};

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(sessionActions, dispatch)
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(AccountPage);



// WEBPACK FOOTER //
// ./src/containers/AccountPage.js